import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import "./doctor_profile.css";
import SideBar from "../Components/SideBar.js";
import DoctorProfileMobile from "./doctor_profile_mobile.js";
import { API_BASE_URL } from "../config.js";

export default function DoctorProfile() {
  const navigate = useNavigate();
  // 1. استخراج id أولاً لتجنب خطأ الترتيب
  const { id } = useParams();
  const location = useLocation();

  // 2. التحقق من دور المستخدم الحالي (سواء من حالة التوجيه أو التخزين المحلي)
  const storedRole = localStorage.getItem("user_role");
  const stateRole = location.state?.role;
  const isAdmin = stateRole === "admin" || storedRole === "admin";

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [notification, setNotification] = useState({ show: false, message: "", type: "" });
  
  const [formData, setFormData] = useState({
    user_id: "",
    real_name: localStorage.getItem("real_name") || "",
    specialization: localStorage.getItem("specialty") || "",
    username: localStorage.getItem("username") || "",
    phon_number: localStorage.getItem("phon_number") || "",
    email: localStorage.getItem("email") || "",
    password: "", 
    city: localStorage.getItem("city") || "",
    hospital_name: localStorage.getItem("hospital") || "",
    hospital_address: localStorage.getItem("hospital_address") || "",
  });

  // تتبع تغيير حجم الشاشة للتجاوب مع الجوال
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // جلب بيانات الطبيب من الخادم
  useEffect(() => {
    const fetchDoctorProfile = async () => {
      const token = localStorage.getItem("access_token");
      if (!token) {
        navigate("/login");
        return;
      }
      const baseUrl = API_BASE_URL || "http://127.0.0.1:8000/api";
      
      const url = (isAdmin && id) 
        ? `${baseUrl}/doctors/${id}/` 
        : `${baseUrl}/doctors/me/`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const docData = await response.json();
          let userData = docData.user || {};
          
          // إذا كان العائد رقم ID (كما يحدث في مسار الأدمن)، نجلب بيانات الـ User المخصصة
          if (typeof docData.user === 'number') {
             const userRes = await fetch(`${baseUrl}/users/${docData.user}/`, {
                headers: { "Authorization": `Bearer ${token}` }
             });
             if (userRes.ok) userData = await userRes.json();
          }

          setFormData(prev => ({
            ...prev,
            user_id: userData.id || "",
            admin_approved: userData.admin_approved || false,
            real_name: userData.real_name || docData.real_name || "",
            username: userData.username || docData.username || "",
            phon_number: userData.phon_number || docData.phon_number || "",
            email: userData.email || docData.email || "",
            specialization: docData.specialization || "",
            hospital_name: docData.hospital_name || "",
            city: docData.city || "",
            hospital_address: docData.hospital_address || "",
          }));

        } else if (response.status === 401) {
          localStorage.removeItem("access_token");
          navigate("/login");
        }
      } catch (err) {
        console.error("Network or parsing error occurred:", err);
      } finally {
        setFetchingData(false);
      }
    };
    fetchDoctorProfile();
  }, [navigate, id, isAdmin]);

  const handleAccountAction = async (actionType) => {
  if (!formData.user_id) {
    showNotification("خطأ: تعذر العثور على معرف الطبيب", "error");
    return;
  }
  
  setLoading(true);
  try {
    const token = localStorage.getItem("access_token");
    const baseUrl = API_BASE_URL || "http://127.0.0.1:8000/api";
    
    // تحديد المسار بناءً على نوع العملية (موافقة أو رفض)
    // لاحظ المسار الجديد: /api/users/{id}/approve/
    const endpoint = actionType === 'approve' ? 'approve' : 'reject';
    
    const response = await fetch(`${baseUrl}/users/${formData.user_id}/${endpoint}/`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      // لا تحتاج لإرسال body لأن الـ Backend سيتكفل بالباقي!
    });

    if (response.ok) {
      if (actionType === 'approve') {
        showNotification("تم تفعيل حساب الطبيب بنجاح!", "success");
        setFormData(prev => ({ ...prev, admin_approved: true }));
      } else {
        showNotification("تم رفض الحساب.", "success");
        setTimeout(() => navigate('/all-doctors'), 1500); 
      }
    } else {
      const errorData = await response.json();
      showNotification(errorData.error || "حدث خطأ أثناء معالجة الطلب.", "error");
    }
  } catch (error) {
    showNotification("فشل الاتصال بالخادم.", "error");
  } finally {
    setLoading(false);
  }
};

  const handleChange = (e) => {
    if (isAdmin) return; // منع التعديل إذا كان المستخدم Admin
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "" });
    }, 4000);
  };

  const handleSave = async () => {
    if (isAdmin) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      
      if (!token) {
        navigate("/login");
        return;
      }

      const payload = {
        real_name: formData.real_name,
        username: formData.username,
        phon_number: formData.phon_number,
        email: formData.email,
        specialization: formData.specialization,
        city: formData.city,
        hospital_name: formData.hospital_name,
        hospital_address: formData.hospital_address,
      };

      if (formData.password) {
        payload.password = formData.password;
      }

      const baseUrl = API_BASE_URL || "http://127.0.0.1:8000/api";
      const url = `${baseUrl}/doctors/me/`;
             
      const response = await fetch(url, {
        method: "PATCH", 
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const updatedData = await response.json();
        
        localStorage.setItem("real_name", updatedData.real_name || formData.real_name);
        localStorage.setItem("username", updatedData.username || formData.username);
        localStorage.setItem("phon_number", updatedData.phon_number || formData.phon_number);
        localStorage.setItem("email", updatedData.email || formData.email);
        localStorage.setItem("specialty", updatedData.specialization || formData.specialization);
        
        setFormData(prev => ({ ...prev, password: "" }));
        showNotification("تم تحديث البيانات بنجاح!", "success");
      } else {
        showNotification("حدث خطأ أثناء التحديث.", "error");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      showNotification("تعذر الاتصال بالخادم.", "error");
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f4f7fb]">
        <div className="text-center">
          <i className="fa-solid fa-circle-notch fa-spin text-3xl text-[#0b4f6f] mb-3"></i>
          <p className="text-gray-600 font-bold">جاري تحميل البيانات...</p>
        </div>
      </div>
    );
  }

  // دعم العرض المخصص للجوال
  if (isMobile && !isAdmin) {
    return (
      <DoctorProfileMobile
        formData={formData}
        handleChange={handleChange}
        handleSave={handleSave}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        loading={loading}
        notification={notification}
      />
    );
  }

  return (
    <div className="profile-wrapper" dir="rtl">
      <SideBar role={isAdmin ? "admin" : "doctor"} />
      <main className="profile-main-content">
        <div className={`toast-notification ${notification.show ? 'show' : ''} ${notification.type}`}>
          {notification.message}
        </div>
        <div className="profile-header-card">
          <div className="header-info">
            <p className="subtitle">{isAdmin ? "الملف الشخصي للطبيب (معاينة المشرف)" : "الملف الشخصي"}</p>
            <h2 className="doctor-name">د. {formData.real_name}</h2>
            <span className="doctor-badge">{formData.specialization}</span>
          </div>
        </div>
        <div className="profile-sections-grid">
          <section className="profile-card">
            <h3 className="card-title">المعلومات الشخصية</h3>
            <div className="form-grid">
              <div className={`input-group ${isAdmin ? "readonly-group" : ""}`}>
                <label>الاسم الكامل</label>
                <div className="input-wrapper">
                  <i className="fa-solid fa-address-card icon"></i>
                  <input 
                    type="text" 
                    name="real_name" 
                    value={formData.real_name} 
                    onChange={handleChange} 
                    readOnly={isAdmin}
                    placeholder="الاسم الكامل" 
                  />
                </div>
              </div>
              <div className="input-group readonly-group">
                <label>اسم المستخدم</label>
                <div className="input-wrapper">
                  <i className="fa-solid fa-user icon"></i>
                  <input type="text" name="username" value={formData.username} readOnly dir="ltr" placeholder="Username" />
                </div>
              </div>
              <div className={`input-group ${isAdmin ? "readonly-group" : ""}`}>
                <label>رقم الهاتف</label>
                <div className="input-wrapper">
                  <i className="fa-solid fa-phone icon"></i>
                  <input 
                    type="tel" 
                    name="phon_number" 
                    value={formData.phon_number} 
                    onChange={handleChange} 
                    readOnly={isAdmin}
                    dir="ltr" 
                    placeholder="09xxxxxxx" 
                  />
                </div>
              </div>
              <div className={`input-group ${isAdmin ? "readonly-group" : ""}`}>
                <label>البريد الإلكتروني</label>
                <div className="input-wrapper">
                  <i className="fa-solid fa-envelope icon"></i>
                  <input 
                    type="email" 
                    name="email" 
                    value={formData.email} 
                    onChange={handleChange} 
                    readOnly={isAdmin}
                    dir="ltr" 
                    placeholder="email@example.com" 
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="profile-card">
            <h3 className="card-title">المعلومات المهنية</h3>
            <div className="form-grid">
              <div className={`input-group ${isAdmin ? "readonly-group" : ""}`}>
                <label>الاختصاص</label>
                <div className="input-wrapper">
                  <i className="fa-solid fa-user-doctor icon"></i>
                  <input 
                    type="text" 
                    name="specialization" 
                    value={formData.specialization} 
                    onChange={handleChange} 
                    readOnly={isAdmin}
                    placeholder="الاختصاص" 
                  />
                </div>
              </div>
              <div className={`input-group ${isAdmin ? "readonly-group" : ""}`}>
                <label>المدينة</label>
                <div className="input-wrapper">
                  <i className="fa-solid fa-location-dot icon"></i>
                  <input 
                    type="text" 
                    name="city"
                    value={formData.city} 
                    onChange={handleChange}
                    readOnly={isAdmin}
                    placeholder="المدينة"
                  />
                </div>
              </div>
              <div className={`input-group ${isAdmin ? "readonly-group" : ""}`}>
                <label>المستشفى</label>
                <div className="input-wrapper">
                  <i className="fa-solid fa-hospital icon"></i>
                  <input 
                    type="text" 
                    name="hospital_name"
                    value={formData.hospital_name} 
                    onChange={handleChange}
                    readOnly={isAdmin}
                    placeholder="المستشفى"
                  />
                </div>
              </div>
              <div className={`input-group ${isAdmin ? "readonly-group" : ""}`}>
                <label>عنوان المستشفى</label>
                <div className="input-wrapper">
                  <i className="fa-solid fa-map icon"></i>
                  <input 
                    type="text" 
                    name="hospital_address"
                    value={formData.hospital_address} 
                    onChange={handleChange}
                    readOnly={isAdmin}
                    placeholder="عنوان المستشفى"
                  />
                </div>
              </div>
            </div>
          </section>

          {!isAdmin && (
            <section className="profile-card security-card">
              <h3 className="card-title">تغيير كلمة المرور</h3>
              <div className="input-group">
                <label>كلمة المرور الجديدة</label>
                <div className="input-wrapper">
                  <i className="fa-solid fa-lock icon"></i>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="أدخل كلمة مرور جديدة للتغيير"
                    autoComplete="new-password"
                  />
                  <i
                    className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'} toggle-password`}
                    onClick={() => setShowPassword(!showPassword)}
                  ></i>
                </div>
              </div>
            </section>
          )}
        </div>

        {/* أزرار الحفظ للطبيب */}
          {!isAdmin && (
            <div className="profile-actions">
              <button className="btn-primary" onClick={handleSave} disabled={loading}>
                {loading ? <i className="fa-solid fa-spinner fa-spin"></i> : "حفظ التغييرات"}
              </button>
            </div>
          )}

          {/* أزرار الموافقة والرفض للأدمن (في حال كان الحساب قيد الانتظار) */}
          {isAdmin && !formData.admin_approved && (
            <div className="profile-actions" style={{ gap: '15px' }}>
              <button 
                className="btn-primary" 
                onClick={() => handleAccountAction('approve')} 
                disabled={loading} 
                style={{ backgroundColor: '#2ecc71' }}
              >
                <i className="fa-solid fa-check"></i> موافقة
              </button>
              
              <button 
                className="btn-primary" 
                onClick={() => handleAccountAction('reject')} 
                disabled={loading} 
                style={{ backgroundColor: '#e74c3c' }}
              >
                <i className="fa-solid fa-xmark"></i> رفض
              </button>
            </div>
          )}
      </main>
    </div>
  );
}